import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Container,
  Paper,
  Chip,
  useTheme,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Fab,
  Zoom,
  Slide,
  Fade,
  Stack,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  School as SchoolIcon,
  Science as ScienceIcon,
  Verified as VerifiedIcon,
  LocalHospital as LocalHospitalIcon,
  Tune as TuneIcon,
  Assessment as AssessmentIcon,
  Water as WaterIcon,
  Grass as GrassIcon,
  Biotech as BiotechIcon,
  Shield as ShieldIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  TrendingUp as TrendingUpIcon,
  Public as PublicIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Groups as GroupsIcon,
  Language as LanguageIcon,
  Lightbulb as LightbulbIcon,
  ArrowForward as ArrowForward
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Define accent color
  const accentColor = '#007bff';

  useEffect(() => {
    setIsVisible(true);

    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const accreditationPrograms = [
    {
      icon: <ScienceIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Testing & Calibration Labs',
      description: 'NABL accreditation for testing Lab / calibration lab (ISO/IEC 17025), Medical Lab (ISO 15189), PTP, RMP',
      image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=400&h=220&fit=crop&crop=center&auto=format&q=80',
      badge: 'Popular',
      badgeColor: 'primary',
      loginAction: () => navigate('/main/login'),
      registerAction: () => navigate('/main/register'),
    },
    // {
    //   icon: <ScienceIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    //   title: 'PPP / FF Arrangements',
    //   description: 'Medical laboratories operating under Operational and Supporting Entity arrangements.',
    //   image: 'https://images.unsplash.com/photo-1579154204601-01588f351e67?w=400&h=220&fit=crop&crop=center&auto=format&q=80',
    //   badge: 'Medical',
    //   badgeColor: 'primary',
    //   loginAction: () => navigate('/main/login'),
    //   registerAction: () => navigate('/main/register-ppp-ffl'),
    // },
    {
      icon: <GrassIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      title: 'Soil Testing Laboratory',
      description: 'Soil Testing Laboratory Program for agricultural and environmental assessment',
      image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=400&h=220&fit=crop&crop=center&auto=format&q=80',
      badge: 'Environmental',
      badgeColor: 'success',
      loginAction: () => window.open('https://nablstlp.qci.org.in/', '_blank'),
      registerAction: () => window.open('https://nablstlp.qci.org.in/registration-process.php', '_blank'),
    },
    {
      icon: <WaterIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      title: 'Water Testing (G-LAP)',
      description: 'Government Drinking Water testing Laboratory Accreditation Program (G-LAP)',
      image: 'https://images.unsplash.com/photo-1576671081837-49000212a370?w=400&h=220&fit=crop&crop=center&auto=format&q=80',
      badge: 'Government',
      badgeColor: 'info',
      loginAction: () => window.open('http://nablglap.qci.org.in/index.php', '_blank'),
      registerAction: () => window.open('http://nablglap.qci.org.in/registration-process.php', '_blank'),
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Product Based Accreditation',
      description: 'Apply now for NABL Product Based Accreditation for comprehensive product testing',
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=220&fit=crop&crop=center&auto=format&q=80',
      badge: 'Comprehensive',
      badgeColor: 'secondary',
      loginAction: () => navigate('/main/login'),
      registerAction: () => window.open('https://nablwp.qci.org.in/Account/Registerproductscheme', '_blank'),
    },
    {
      icon: <LocalHospitalIcon sx={{ fontSize: 40, color: 'error.main' }} />,
      title: 'Medical Entry Level Testing',
      description: 'Apply Now for NABL Medical Entry Level Testing M(EL)T Labs Program',
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=220&fit=crop&crop=center&auto=format&q=80',
      badge: 'Medical',
      badgeColor: 'error',
      loginAction: () => window.open('https://nablmelt.qci.org.in/Laboratory/login.php', '_blank'),
      registerAction: () => window.open('https://nablmelt.qci.org.in/Laboratory/new-scheme/registration-process.php', '_blank'),
    },
    {
      icon: <BiotechIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      title: 'COVID-19 Testing',
      description: 'NABL Accreditation for medical testing laboratories of RT-PCR RNA Virus/ COVID-19',
      image: 'https://images.unsplash.com/photo-1584362917165-526a968579e8?w=400&h=220&fit=crop&crop=center&auto=format&q=80',
      badge: 'COVID-19',
      badgeColor: 'warning',
      loginAction: () => window.open('https://nablwp.qci.org.in/Account/LoginMED', '_blank'),
      registerAction: () => window.open('https://nablwp.qci.org.in/Account/RegisterMed', '_blank'),
    },
  ];

  const features = [
    { icon: <SecurityIcon color="primary" />, text: 'ISO/IEC 17011 Compliant' },
    { icon: <PublicIcon color="primary" />, text: 'International Standards' },
    { icon: <VerifiedIcon color="primary" />, text: 'Trusted Accreditation' },
    { icon: <SpeedIcon color="primary" />, text: 'Fast Processing' },
  ];



  return (
    <Box sx={{ position: 'relative', py: { xs: 6, md: 8, lg: 12 } }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Fade in={isVisible} timeout={1000}>
          <Box id="home" sx={{ mb: 8, scrollMarginTop: '100px' }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Slide direction="right" in={isVisible} timeout={800}>
                  <Box>
                    <img src='/main/images/v1_9.png' alt="NABL Logo" style={{
                      width: 'auto',
                      height: '120px',
                      filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))'
                    }} />
                    <Typography
                      variant="h1"
                      component="h1"
                      gutterBottom
                      sx={{
                        fontWeight: 800,
                        background: 'linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 2,
                        fontSize: { xs: '2.5rem', md: '3.5rem', lg: '3.5rem' },
                        lineHeight: 1.1,
                      }}
                    >
                      Welcome to NABL
                    </Typography>
                    <Typography
                      variant="h4"
                      component="h2"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        color: '#3a579d',
                        mb: 1,
                        fontSize: { xs: '1.5rem', md: '1.5rem' },
                      }}
                    >
                      National Accreditation Board for Testing and Calibration Laboratories
                    </Typography>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      paragraph
                      sx={{
                        mb: 2,
                        fontWeight: 500,
                        fontSize: { xs: '1rem', md: '1.25rem' }
                      }}
                    >
                      A Constituent Board of Quality Council of India
                    </Typography>
                    {/* <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                      sx={{
                        mb: 4,
                        lineHeight: 1.7,
                        fontSize: '1.1rem',
                        maxWidth: '600px'
                      }}
                    >
                      Ensuring quality and competence in testing and calibration services across India through world-class accreditation standards and innovative digital solutions.
                    </Typography> */}

                    {/* Feature Chips */}
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 4, flexWrap: 'wrap' }}>
                      {features.map((feature, index) => (
                        <Zoom in={isVisible} timeout={1000 + index * 200} key={index}>
                          <Chip
                            icon={feature.icon}
                            label={feature.text}
                            variant="outlined"
                            color="primary"
                            size="medium"
                            sx={{
                              py: 2,
                              px: 1,
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(0, 2, 76, 0.2)',
                              },
                              transition: 'all 0.3s ease',
                            }}
                          />
                        </Zoom>
                      ))}
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<ArrowForward />}
                        onClick={() => window.open('https://nabl-india.org/', '_blank')}
                        sx={{
                          px: 4,
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          borderRadius: '30px',
                          background: `linear-gradient(135deg, ${accentColor} 0%, #0056b3 100%)`,
                          boxShadow: '0 8px 24px rgba(0, 86, 179, 0.3)',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                            transition: 'left 0.6s ease',
                          },
                          '&:hover': {
                            background: `linear-gradient(135deg, #0056b3 0%, ${accentColor} 100%)`,
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 32px rgba(0, 86, 179, 0.4)',
                            '&::before': {
                              left: '100%',
                            },
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Learn More About Accreditation
                      </Button>
                    </Stack>
                  </Box>
                </Slide>
              </Grid>

              <Grid item xs={12} md={6}>
                <Slide direction="left" in={isVisible} timeout={800}>
                  <Box sx={{ textAlign: 'center', position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'inline-block',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '-20px',
                          left: '-20px',
                          right: '-20px',
                          bottom: '-20px',
                          background: 'linear-gradient(135deg, #00024c20, #1a2b6b20)',
                          borderRadius: '30px',
                          zIndex: -1,
                        },
                      }}
                    >
                      <img
                        src="/main/images/hero-sec.png"
                        alt="NABL Logo"
                        style={{
                          borderRadius: '20px',
                          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                          width: '100%',
                          height: 'auto',
                          maxWidth: '500px',
                        }}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/500x300/00024c/ffffff?text=NABL+Accreditation';
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = 'scale(1.02)';
                          e.target.style.boxShadow = '0 25px 80px rgba(0, 0, 0, 0.2)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = 'scale(1)';
                          e.target.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.15)';
                        }}
                      />
                    </Box>
                  </Box>
                </Slide>
              </Grid>
            </Grid>
          </Box>
        </Fade>

        {/* Important Notification */}
        <Slide direction="up" in={isVisible} timeout={1000}>
          <Alert
            severity="warning"
            sx={{
              mb: 6,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(227, 160, 8, 0.2)',
              border: '1px solid #ffcc02',
              overflow: 'hidden',
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }} >
              <Typography variant="body2" sx={{ fontWeight: 700, textWrap: 'nowrap', mr: 2 }}>
                📢 Important Notification
              </Typography>
              <Box
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  position: 'relative',
                  '&:hover .marquee-content': {
                    animationPlayState: 'paused',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  className="marquee-content"
                  sx={{
                    lineHeight: 1.6,
                    display: 'inline-block',
                    paddingLeft: '100%',
                    animation: 'marquee 25s linear infinite',
                    '@keyframes marquee': {
                      '0%': {
                        transform: 'translate3d(0, 0, 0)',
                      },
                      '100%': {
                        transform: 'translate3d(-100%, 0, 0)',
                      },
                    },
                  }}
                >
                  Laboratories which have been registered on the Portal but not filled the details other than basic details
                  or having multiple registrations with same details or not active for a long time, the login credentials
                  of such type of laboratories will stands cancelled/ blocked w.e.f 01.12.2018. Thereafter, laboratory
                  has to register afresh. Now, testing and calibration laboratories can apply only as per{' '}
                  <strong>ISO/IEC 17025: 2017</strong>. No hard copy of applications will be accepted.
                </Typography>
              </Box>
            </div>
          </Alert>
        </Slide>

        {/* Apply For Accreditation Section */}
        <Fade in={isVisible} timeout={1400}>
          <Box id="accreditation" sx={{ mb: 8, scrollMarginTop: '100px' }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h3"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                Apply For Accreditation
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                paragraph
                sx={{
                  maxWidth: 800,
                  mx: 'auto',
                  mb: 2,
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                }}
              >
                Choose from our comprehensive range of accreditation programs designed to meet your laboratory's specific needs
              </Typography>
              <Box
                sx={{
                  width: 80,
                  height: 4,
                  background: 'linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)',
                  borderRadius: 2,
                  mx: 'auto',
                }}
              />
            </Box>

            <Grid container spacing={4}>
              {accreditationPrograms.map((program, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Zoom in={isVisible} timeout={1400 + index * 200}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 4,
                        overflow: 'hidden',
                        position: 'relative',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                        border: '1px solid #e3f2fd',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-12px) scale(1.02)',
                          boxShadow: '0 20px 60px rgba(0, 2, 76, 0.2)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          height: 220,
                          backgroundImage: `linear-gradient(135deg, rgba(0, 2, 76, 0.1) 0%, rgba(26, 43, 107, 0.1) 100%), url(${program.image})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                      >
                        {/* Badge */}
                        <Chip
                          label={program.badge}
                          color={program.badgeColor}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                          }}
                        />

                        {/* Icon */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 16,
                            left: 16,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '50%',
                            p: 1.5,
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                          }}
                        >
                          {program.icon}
                        </Box>
                      </Box>

                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Typography
                          variant="h5"
                          gutterBottom
                          sx={{
                            fontWeight: 700,
                            mb: 2,
                            color: 'text.primary',
                            fontSize: '1.3rem',
                          }}
                        >
                          {program.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          paragraph
                          sx={{
                            mb: 3,
                            lineHeight: 1.7,
                            flexGrow: 1,
                            fontSize: '0.95rem',
                          }}
                        >
                          {program.description}
                        </Typography>

                        <Stack direction="row" spacing={1.5}>
                          <Tooltip title="Access existing account">
                            <Button
                              variant="outlined"
                              size="medium"
                              onClick={program.loginAction}
                              sx={{
                                flex: 1,
                                py: 1,
                                fontWeight: 600,
                                borderWidth: 2,
                                borderColor: accentColor,
                                color: accentColor,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: '-100%',
                                  width: '100%',
                                  height: '100%',
                                  background: 'linear-gradient(90deg, transparent, rgba(0, 86, 179, 0.1), transparent)',
                                  transition: 'left 0.6s ease',
                                },
                                '&:hover': {
                                  borderWidth: 2,
                                  borderColor: '#0056b3',
                                  color: '#0056b3',
                                  transform: 'translateY(-1px)',
                                  '&::before': {
                                    left: '100%',
                                  },
                                },
                                transition: 'all 0.3s ease',
                              }}
                            >
                              Login
                            </Button>
                          </Tooltip>
                          <Tooltip title="Create new account">
                            <Button
                              variant="contained"
                              size="medium"
                              onClick={program.registerAction}
                              sx={{
                                flex: 1,
                                py: 1,
                                fontWeight: 600,
                                background: `linear-gradient(135deg, ${accentColor} 0%, #0056b3 100%)`,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: '-100%',
                                  width: '100%',
                                  height: '100%',
                                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                                  transition: 'left 0.6s ease',
                                },
                                '&:hover': {
                                  background: `linear-gradient(135deg, #0056b3 0%, ${accentColor} 100%)`,
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 8px 24px rgba(0, 86, 179, 0.3)',
                                  '&::before': {
                                    left: '100%',
                                  },
                                },
                                transition: 'all 0.3s ease',
                              }}
                            >
                              Register
                            </Button>
                          </Tooltip>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Fade>

        {/* About NABL and Portal Info */}
        <Fade in={isVisible} timeout={1600}>
          <Box id="about" sx={{ scrollMarginTop: '100px' }}>
            <Grid container spacing={4} sx={{ mb: 8 }}>
              <Grid item xs={12} md={6}>
                <Slide direction="right" in={isVisible} timeout={1600}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                      border: '1px solid #e3f2fd',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(0, 2, 76, 0.15)',
                      },
                      transition: 'all 0.4s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <LightbulbIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                      <Typography
                        variant="h4"
                        gutterBottom
                        sx={{ fontWeight: 700, color: 'primary.main', mb: 0 }}
                      >
                        About NABL
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: '1rem' }}>
                      National Accreditation Board for Testing and Calibration Laboratories (NABL), a Constituent
                      Board of Quality Council of India has been established with the objective of providing Government,
                      Industry Associations and Industry in general with a scheme of Conformity Assessment Body's
                      accreditation which involves third-party assessment of the technical competence of testing
                      including medical and calibration laboratories, proficiency testing providers and reference
                      material producers.
                    </Typography>
                  </Paper>
                </Slide>
              </Grid>

              <Grid item xs={12} md={6}>
                <Slide direction="left" in={isVisible} timeout={1600}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                      border: '1px solid #e3f2fd',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(0, 2, 76, 0.15)',
                      },
                      transition: 'all 0.4s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <LanguageIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                      <Typography
                        variant="h4"
                        gutterBottom
                        sx={{ fontWeight: 700, color: 'primary.main', mb: 0 }}
                      >
                        NABL Portal
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: '1rem' }}>
                      As technology change accelerates exponentially and new digital platforms are emerging,
                      NABL aim to provide with an end-to-end solution to its stakeholders by developing online portal.
                      This portal serve as a way for stakeholders to gain secure web access to systems that allows
                      them to provide a one-stop window for the entire accreditation process like submission of
                      online application, making all the assessment charges etc.
                    </Typography>
                  </Paper>
                </Slide>
              </Grid>
            </Grid>
          </Box>
        </Fade>

        {/* Contact Information */}
        <Fade in={isVisible} timeout={1800}>
          <Box id="contact" sx={{ scrollMarginTop: '100px' }}>
            <Paper
              elevation={6}
              sx={{
                p: 5,
                background: 'linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)',
                color: 'white',
                textAlign: 'center',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="rgba(255,255,255,0.05)" fill-opacity="1" fill-rule="evenodd"%3E%3Cpath d="m0 40l40-40h-40v40zm40 0v-40h-40l40 40z"/%3E%3C/g%3E%3C/svg%3E")',
                  opacity: 0.1,
                },
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                  🤝 For Any Support please Contact us at:
                </Typography>
                <Grid container spacing={4} justifyContent="center">
                  <Grid item xs={12} md={6}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      p: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.15)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}>
                      <EmailIcon sx={{ fontSize: 28 }} />
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.9rem' }}>
                          Email Support
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          supportwp@nabl.qcin.org
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      p: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.15)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}>
                      <PhoneIcon sx={{ fontSize: 28 }} />
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.9rem' }}>
                          Phone support for medical applications
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          011-40032401, 011-40032402
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      p: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.15)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}>
                      <PhoneIcon sx={{ fontSize: 28 }} />
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.9rem' }}>
                          Phone support for calibration applications
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          011-40032483, 011-40050505
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      p: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.15)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '1rem' }}>
                          © {new Date().getFullYear()} - National Accreditation Board for Testing and Calibration Laboratories (NABL)
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: { xs: 2, sm: 4 },
                            flexDirection: { xs: 'column', sm: 'row' },
                            mt: 1,
                          }}
                        >
                          <Box
                            component="a"
                            href="https://nablwpnew.qci.org.in/PrivacyPolicy"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              color: 'rgba(255, 255, 255, 0.8)',
                              textDecoration: 'none',
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              padding: '5px 10px',
                              borderRadius: '25px',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                color: 'white',
                                background: `linear-gradient(135deg, ${accentColor} 0%, #0056b3 100%)`,
                                border: `1px solid ${accentColor}`,
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 15px rgba(0, 123, 255, 0.3)',
                              },
                            }}
                          >
                            <SecurityIcon sx={{ fontSize: 18 }} />
                            Privacy Policy
                          </Box>

                          <Box
                            component="a"
                            href="https://nablwpnew.qci.org.in/Disclaimer"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              color: 'rgba(255, 255, 255, 0.8)',
                              textDecoration: 'none',
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              padding: '5px 10px',
                              borderRadius: '25px',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                color: 'white',
                                background: `linear-gradient(135deg, ${accentColor} 0%, #0056b3 100%)`,
                                border: `1px solid ${accentColor}`,
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 15px rgba(0, 123, 255, 0.3)',
                              },
                            }}
                          >
                            <WarningIcon sx={{ fontSize: 18 }} />
                            Disclaimer
                          </Box>

                          <Box
                            component="a"
                            href="https://nablwpnew.qci.org.in/Refund"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              color: 'rgba(255, 255, 255, 0.8)',
                              textDecoration: 'none',
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              padding: '5px 10px',
                              borderRadius: '25px',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                color: 'white',
                                background: `linear-gradient(135deg, ${accentColor} 0%, #0056b3 100%)`,
                                border: `1px solid ${accentColor}`,
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 15px rgba(0, 123, 255, 0.3)',
                              },
                            }}
                          >
                            <ArrowForward sx={{ fontSize: 18 }} />
                            Refund and Cancellation
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Container>

      {/* Scroll to Top Button */}
      <Zoom in={showScrollTop}>
        <Fab
          color="primary"
          size="medium"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            background: `linear-gradient(135deg, ${accentColor} 0%, #0056b3 100%)`,
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
              transition: 'left 0.6s ease',
            },
            '&:hover': {
              background: `linear-gradient(135deg, #0056b3 0%, ${accentColor} 100%)`,
              transform: 'scale(1.1)',
              '&::before': {
                left: '100%',
              },
            },
            transition: 'all 0.3s ease',
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
    </Box>
  );
};

export default HomePage;
